<script lang="ts" setup>
import { shallowRef, ref } from 'vue';
import { useRouter } from 'vue-router';
import VueDatePicker, { type DatePickerInstance } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import Avatar from 'vue3-avatar';
import FormData from 'form-data';
import axios from 'axios';

import TextField from '../common/components/text-field.component.vue';

import username from './components/username.component.vue';
import FirstName from './components/first-name.component.vue';

import { validateNoSpecialCharacters } from '@/common/validators/validate-no-special-characters.ts';
import apiClient from '@/common/api-client';

const $router = useRouter();
const { data } = await apiClient.get('/profile');

if (!data.sexo) {
  data.sexo = 'M';
}

if (!data.genero) {
  data.genero = 'M';
}
const errorFields = ref({
  username: '',
  firstName: '',
});

const datepicker = ref<DatePickerInstance>();

function formIsValid() {
  return Object.values(errorFields.value).every(v => v === '');
}

const fileInput = shallowRef();

const dataRef = ref(data);
function handleError(error: string) {
  const err = error.split(':');
  errorFields.value[err[0]] = err[1];
}
function updateProfile(ev) {
  Object.keys(ev).forEach(key => {
    errorFields.value[key] = '';
    dataRef.value[key] = ev[key];
  });
  dataRef.value = { ...dataRef.value, ...ev };
}
async function submitForm() {
  if (formIsValid()) {
    const { foto, ...rest } = dataRef.value;
    await apiClient.put(`/profile`, { ...rest });
    $router.push('/');
  }
}
const apiUrl = import.meta.env.VITE_API_URL;
async function handleFileUpload(e: any) {
  const file = e.target.files[0];
  const formData = new FormData();
  formData.append('foto', file);
  await axios.post(`${apiUrl}/profile/foto`, formData);
  const nuevoPerfil = await apiClient.get(`/profile`);
  dataRef.value.foto = nuevoPerfil.data.foto;
}
</script>
<template>
  <section class="mx-auto max-w-3xl px-4 py-10">
    <header class="mb-6">
      <h1 class="text-3xl font-bold tracking-tight text-gray-900">Profile</h1>
      <p class="mt-1 text-sm text-gray-500">Actualiza tu información personal.</p>
    </header>

    <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow">
      <div class="p-6 sm:p-8">
        <div class="space-y-5">
          <avatar
            :imageSrc="`${apiUrl}/fotos/${dataRef.foto}`"
            :name="`${dataRef.nombre} ${dataRef.apellido}`"
            :customAvatarStyle="{ height: '100px', width: '100px' }"
            @click="() => fileInput.click()"
          ></avatar>
          <input
            data-testid="upload"
            type="file"
            style="display: none"
            ref="fileInput"
            @change="handleFileUpload"
          />
          <username :username="dataRef.usuario" @error="handleError" @update="updateProfile" />
          <firstName :firstName="dataRef.nombre" @error="handleError" @update="updateProfile" />
          <text-field
            :validator="validateNoSpecialCharacters"
            :value="dataRef.apellido"
            field="apellido"
            label="Apellido"
            @error="handleError"
            @update="updateProfile"
          />

          <div>
            <label class="mb-1 block text-sm font-medium">Fecha de nacimiento</label>
            <vue-date-picker
              ref="datepicker"
              v-model="dataRef.fechaNacimiento"
              data-testid="datepicker-input"
              text-input
              teleport
              auto-apply
              :enable-time-picker="false"
              format="dd/MM/yyyy"
            />
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium">Sexo</label>
            <select v-model="dataRef.sexo">
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
              <option value="I">Intersexual</option>
            </select>

            <label class="mb-1 block text-sm font-medium">G&eacute;nero</label>
            <select v-model="dataRef.genero">
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
              <option value="NB">No Binario</option>
              <option value="O">Otro</option>
            </select>
          </div>
        </div>

        <button
          class="mt-6 inline-flex items-center justify-center rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
          data-testid="submit"
          @click="submitForm()"
        >
          Submit
        </button>
      </div>
    </div>
  </section>
</template>

<style scoped></style>
